import React, { useEffect, useState } from "react";
import { API_KEY, CRUDPayments } from "../../hooks/APIHooks";
import { IoCheckmarkSharp, IoClose, IoTimeOutline } from "react-icons/io5";

export default function AllPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusUpdate, setStatusUpdate] = useState({});
  const [filterStatus, setFilterStatus] = useState("Pending"); // Default to All as requested

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${CRUDPayments}?api_key=${API_KEY}`);
      const data = await res.json();
      if (data.status === "success") {
        setPayments(data.withdraws);
        const statusMap = {};
        data.withdraws.forEach((p) => (statusMap[p.id] = p.status));
        setStatusUpdate(statusMap);
      } else {
        alert("Failed to fetch payments");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching payments");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const updateStatus = async (id) => {
    const newStatus = statusUpdate[id];
    try {
      const res = await fetch(`${CRUDPayments}?api_key=${API_KEY}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status: newStatus }),
      });
      const data = await res.json();
      if (data.status === "success") {
        alert("Status updated successfully");
        fetchPayments();
      } else {
        alert("Failed to update status");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating status");
    }
  };

  const filteredPayments = payments.filter((p) => {
    if (filterStatus === "All") return true;
    return p.status === filterStatus;
  });

  if (loading)
    return (
      <div className="p-6 text-center text-gray-500 animate-pulse">
        Loading payments...
      </div>
    );

  return (
    <div className="p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-2xl font-semibold text-primary">All Payments</h2>
        <select
          className="border px-3 py-2 rounded text-sm bg-white shadow-sm focus:outline-none"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
            <tr>
              <th className="p-3 text-left">ID</th>
              <th className="p-3 text-left">User Info</th>
              <th className="p-3 text-left">Address</th>
              <th className="p-3 text-left">Bank Details</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredPayments.length > 0 ? (
              filteredPayments.map((p) => (
                <tr key={p.id} className="bg-gray-50">
                  <td className="p-3 font-medium text-gray-800">{p.id}</td>
                  <td className="p-3 space-y-1">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-gray-500">{p.phonenumber}</div>
                    <div className="text-gray-500">{p.emailaddress}</div>
                  </td>
                  <td className="p-3 text-gray-700">{p.address}</td>
                  <td className="p-3 space-y-1 text-gray-700">
                    <div>Bank: {p.bank_name}</div>
                    <div>Account: {p.bank_account_number}</div>
                    <div>IFSC: {p.ifsc_code}</div>
                    <div>UPI: {p.upi_id}</div>
                  </td>
                  <td className="p-3 text-green-600 font-semibold">
                    ₹{p.amount}{" "}
                    <span className="text-gray-500 text-sm font-normal">
                      (after charges: ₹{(p.amount * 0.95).toFixed(2)})
                    </span>
                  </td>

                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {["Pending", "Completed", "Rejected"].map((status) => {
                        const Icon =
                          status === "Pending"
                            ? IoTimeOutline
                            : status === "Completed"
                            ? IoCheckmarkSharp
                            : IoClose;

                        const isActive = statusUpdate[p.id] === status;
                        const statusColors = {
                          Pending: "bg-blue-100 text-blue-700",
                          Completed: "bg-green-100 text-green-700",
                          Rejected: "bg-red-100 text-red-700",
                        };
                        const activeStyles = isActive
                          ? statusColors[status]
                          : "bg-gray-100 text-gray-600";

                        return (
                          <button
                            key={status}
                            onClick={() =>
                              setStatusUpdate({
                                ...statusUpdate,
                                [p.id]: status,
                              })
                            }
                            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs transition-all duration-200 ${activeStyles}`}
                          >
                            <Icon className="text-sm" />
                            {status}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => updateStatus(p.id)}
                      className="bg-primary hover:bg-primary-dark text-white px-4 py-1 rounded text-xs shadow transition"
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500">
                  No {filterStatus.toLowerCase()} payments found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
